// src/pages/meeting/meetingupdate.js
import jsPDF from "jspdf";
import notoSansRegularUrl from "./fonts/NotoSans-Regular.ttf";
import notoSansSemiboldUrl from "./fonts/NotoSans-SemiBold.ttf";
import beetinqLogoWhiteUrl from "../../images/beetinq/beetinqlogowhite.png";

/**
 * Toplantı verilerini 3 ayrı PDF olarak indirir:
 * 1) Toplantı Özeti
 * 2) Toplantı Dökümü
 * 3) Toplantı Konuşma Detayları
 */

export async function exportMeetingToPDF({
  meeting,
  agendas = [],
  decisions = [],
  users = [],
  notes = [],
  speakers = [],
  aiDetail = {},
  lionNotesBySection = null,
  section = "all",
}) {
  void notes;
  void lionNotesBySection;

  const fontCache = exportMeetingToPDF._fontCache || new Map();
  exportMeetingToPDF._fontCache = fontCache;
  const assetCache = exportMeetingToPDF._assetCache || new Map();
  exportMeetingToPDF._assetCache = assetCache;

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  };

  const fontUrlToBase64 = async (url) => {
    if (fontCache.has(url)) return fontCache.get(url);
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    fontCache.set(url, base64);
    return base64;
  };

  const assetUrlToDataUrl = async (url) => {
    if (assetCache.has(url)) return assetCache.get(url);
    const response = await fetch(url);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    assetCache.set(url, dataUrl);
    return dataUrl;
  };

  const [NOTO_SANS_REGULAR, NOTO_SANS_SEMIBOLD, BEETINQ_LOGO_WHITE] = await Promise.all([
    fontUrlToBase64(notoSansRegularUrl),
    fontUrlToBase64(notoSansSemiboldUrl),
    assetUrlToDataUrl(beetinqLogoWhiteUrl).catch(() => null),
  ]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return `${date.toLocaleDateString("tr-TR")} ${date.toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch {
      return "";
    }
  };

  const formatTime = (timeValue) => {
    if (!timeValue) return "";
    try {
      const time = new Date(timeValue);
      return time.toLocaleTimeString("tr-TR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const formatSeconds = (sec) => {
    const mins = Math.floor((sec || 0) / 60);
    const secs = Math.floor((sec || 0) % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const extractDecisionOrder = (value = "") => {
    const text = String(value);
    const match = text.match(/karar\s*[-:]?\s*(\d+)/i);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  };

  const hasDecisionOrderInTitle = (value = "") => /karar\s*[-:]?\s*\d+/i.test(String(value));
  const isGenericDecisionTitle = (value = "") => /^\s*karar\s*[-:]?\s*\d+\s*$/i.test(String(value));

  const createPdfDoc = (reportTitle) => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    doc.addFileToVFS("NotoSans-Regular.ttf", NOTO_SANS_REGULAR);
    doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
    doc.addFileToVFS("NotoSans-SemiBold.ttf", NOTO_SANS_SEMIBOLD);
    doc.addFont("NotoSans-SemiBold.ttf", "NotoSans", "bold");
    doc.setFont("NotoSans", "normal");

    const margin = 44;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    let y = margin + 10;
    let pageNumber = 1;

    const colors = {
      primary: [24, 85, 170],
      secondary: [24, 36, 56],
      text: [28, 36, 52],
      muted: [92, 104, 124],
      lightGray: [247, 250, 255],
      border: [212, 222, 236],
    };

    const addFooter = () => {
      doc.setFont("NotoSans", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...colors.muted);
      doc.text(`Sayfa ${pageNumber}`, pageWidth - margin - 30, pageHeight - 20);
    };

    const prepareNewPage = () => {
      if (doc.GState) doc.setGState(new doc.GState({ opacity: 1 }));
      doc.setFont("NotoSans", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...colors.text);
      y = margin + 12;
    };

    const newPage = () => {
      doc.addPage();
      pageNumber += 1;
      prepareNewPage();
      addFooter();
      // Footer 9pt kullandığı için, içerik tipografisini tekrar uygula.
      doc.setFont("NotoSans", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...colors.text);
    };

    const checkPageBreak = (neededSpace = 50) => {
      if (y + neededSpace > pageHeight - 60) newPage();
    };

    const addHeaderAd = () => {
      const bandHeight = 60;
      doc.setFillColor(...colors.primary);
      doc.rect(0, 0, pageWidth, bandHeight, "F");

      if (BEETINQ_LOGO_WHITE) {
        doc.addImage(BEETINQ_LOGO_WHITE, "PNG", margin, 17, 120, 26);
      } else {
        doc.setFont("NotoSans", "bold");
        doc.setFontSize(20);
        doc.setTextColor(255, 255, 255);
        doc.text("beetinq", margin, 37);
      }

      doc.setFont("NotoSans", "normal");
      doc.setFontSize(11);
      doc.setTextColor(230, 236, 245);
      doc.text(reportTitle, pageWidth - margin, 37, { align: "right" });

      y = bandHeight + 24;
      doc.setTextColor(...colors.text);
      doc.setFont("NotoSans", "normal");
      doc.setFontSize(11);
    };

    const addHeader = (text) => {
      checkPageBreak(50);
      doc.setFontSize(16);
      doc.setFont("NotoSans", "bold");
      doc.setTextColor(...colors.secondary);
      doc.setFillColor(...colors.primary);
      doc.rect(margin, y - 6, 4, 24, "F");
      doc.text(text, margin + 12, y + 12);
      y += 34;
    };

    const addText = (text, indent = 0) => {
      const safeText = !text || text === "-" ? "Bilgi mevcut değil" : text;
      doc.setFontSize(11);
      doc.setFont("NotoSans", "normal");
      doc.setTextColor(...colors.text);
      const lines = doc.splitTextToSize(safeText, usableWidth - indent);
      lines.forEach((line) => {
        checkPageBreak(18);
        doc.text(line, margin + indent, y);
        y += 15;
      });
      y += 10;
    };

    const addList = (items, numbered = false, itemTopSpacing = 0) => {
      if (!items?.length) return;
      doc.setFontSize(11);
      doc.setFont("NotoSans", "normal");
      doc.setTextColor(...colors.text);
      items.forEach((item, index) => {
        if (!item) return;
        if (index > 0 && itemTopSpacing > 0) y += itemTopSpacing;
        checkPageBreak(24);
        const bullet = numbered ? `${index + 1}.` : "•";
        const cleanItem = item.toString();
        doc.setTextColor(...colors.primary);
        doc.text(bullet, margin + 5, y);
        doc.setTextColor(...colors.text);
        const lines = doc.splitTextToSize(cleanItem, usableWidth - 25);
        lines.forEach((line, lineIndex) => {
          if (lineIndex > 0) checkPageBreak(15);
          doc.text(line, margin + 20, y);
          if (lineIndex < lines.length - 1) y += 15;
        });
        y += 18;
      });
      y += 7;
    };

    const addMeetingInfoBox = () => {
      checkPageBreak(96);
      doc.setFillColor(...colors.lightGray);
      doc.roundedRect(margin, y, usableWidth, 82, 8, 8, "F");
      doc.setDrawColor(...colors.border);
      doc.roundedRect(margin, y, usableWidth, 82, 8, 8, "S");

      doc.setFont("NotoSans", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...colors.secondary);
      doc.text("Toplantı Bilgileri", margin + 14, y + 20);

      doc.setFont("NotoSans", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...colors.text);
      doc.text(`Başlık: ${meeting?.title || "Toplantı"}`, margin + 14, y + 40);
      doc.text(`Başlangıç: ${formatDate(meeting?.startDate)}`, margin + 14, y + 57);
      doc.text(`Bitiş: ${formatDate(meeting?.endDate)}`, margin + 14, y + 74);
      y += 96;
    };

    const addDecisionList = (items = []) => {
      if (!items?.length) return;
      const numberX = margin + 5;
      const contentX = margin + 18;
      const contentWidth = pageWidth - contentX - margin;

      items.forEach((item, idx) => {
        const rawTitle = (item?.title || "").trim();
        const rawDetail = (item?.details || "").trim();
        const hasOrderInTitle = hasDecisionOrderInTitle(rawTitle);
        const hideGenericTitle = isGenericDecisionTitle(rawTitle);
        const lineNo = `${idx + 1}.`;
        const titleText = rawTitle || "Karar";
        const shouldShowNumber = hideGenericTitle || !hasOrderInTitle;

        if (idx > 0) y += 6;
        checkPageBreak(30);
        const titleX = hasOrderInTitle ? contentX : contentX;

        if (shouldShowNumber) {
          doc.setFont("NotoSans", "normal");
          doc.setFontSize(11);
          doc.setTextColor(...colors.primary);
          doc.text(lineNo, numberX, y);
        }

        if (!hideGenericTitle) {
          doc.setFont("NotoSans", "bold");
          doc.setFontSize(12);
          doc.setTextColor(...colors.primary);
          const displayTitle = hasOrderInTitle ? titleText : titleText;
          const titleLines = doc.splitTextToSize(displayTitle, contentWidth);
          titleLines.forEach((line, i) => {
            if (i > 0) checkPageBreak(16);
            doc.text(line, titleX, y);
            if (i < titleLines.length - 1) y += 15;
          });
          y += 14;
        }

        if (rawDetail) {
          doc.setFont("NotoSans", "normal");
          doc.setFontSize(11);
          doc.setTextColor(...colors.text);
          const detailLines = doc.splitTextToSize(rawDetail, contentWidth);
          detailLines.forEach((line, i) => {
            if (i > 0) checkPageBreak(15);
            doc.text(line, contentX, y);
            if (i < detailLines.length - 1) y += 15;
          });
          y += 14;
        } else {
          y += 6;
        }
      });
      y += 6;
    };

    addHeaderAd();

    return { doc, addFooter, addHeader, addText, addList, addMeetingInfoBox, addDecisionList };
  };

  const cleanTitle = (meeting?.title || "Toplantı")
    .replace(/[^\w\s-]/gi, "")
    .replace(/\s+/g, "_")
    .substring(0, 50);

  // 1) Toplantı Özeti
  if (section === "all" || section === "summary") {
    const { doc, addFooter, addHeader, addText, addList, addMeetingInfoBox, addDecisionList } = createPdfDoc("Toplantı Özeti");
    addMeetingInfoBox();

    if (users?.length) {
      addHeader("Katılımcılar");
      const userItems = users.map((user) => {
        const name = `${user?.name || ""} ${user?.surName || ""}`.trim() || "İsimsiz";
        const company = user?.businessName ? ` - ${user.businessName}` : "";
        const job = user?.job ? ` / ${user.job}` : "";
        return `${name}${company}${job}`;
      });
      addList(userItems);
    }

    addHeader("Özet");
    if (aiDetail?.summary) {
      addText(aiDetail.summary, 10);
    } else if (meeting?.details) {
      addText(meeting.details, 10);
    } else {
      addText("Toplantı özeti bulunamadı.", 10);
    }

    const agendaItems = agendas?.map((item) => {
      const startTime = formatTime(item?.startTime);
      const endTime = formatTime(item?.endTime);
      const timeStr = startTime && endTime ? `${startTime}-${endTime}: ` : "";
      const title = item?.title || "Başlıksız";
      const details = item?.details ? ` - ${item.details}` : "";
      return `${timeStr}${title}${details}`;
    });

    if (agendaItems?.length) {
      addHeader("Gündem");
      addList(agendaItems, true, 5);
    } else if (aiDetail?.agenda?.length) {
      addHeader("Gündem");
      addList(aiDetail.agenda, true, 5);
    }

    const sortedDecisions = [...(decisions || [])].sort((a, b) => {
      const aOrder = Math.min(
        extractDecisionOrder(a?.title),
        extractDecisionOrder(a?.details),
      );
      const bOrder = Math.min(
        extractDecisionOrder(b?.title),
        extractDecisionOrder(b?.details),
      );
      return aOrder - bOrder;
    });

    if (sortedDecisions?.length) {
      addHeader("Kararlar");
      addDecisionList(sortedDecisions);
    } else if (aiDetail?.decisions?.length) {
      addHeader("Kararlar");
      addList(aiDetail.decisions, true);
    }

    addFooter();
    doc.save(`${cleanTitle}_01_Ozet.pdf`);
  }

  // 2) Toplantı Dökümü
  if (section === "all" || section === "transcript") {
    const { doc, addFooter, addHeader, addText, addMeetingInfoBox } = createPdfDoc("Toplantı Dökümü");
    addMeetingInfoBox();
    addHeader("Toplantı Dökümü");
    addText(meeting?.meetingTranscripts || "Toplantı dökümü bulunamadı.", 10);
    addFooter();
    doc.save(`${cleanTitle}_02_Dokum.pdf`);
  }

  // 3) Toplantı Konuşma Detayları
  if (section === "all" || section === "speech") {
    const { doc, addFooter, addHeader, addList, addMeetingInfoBox } = createPdfDoc("Toplantı Konuşma Detayları");
    addMeetingInfoBox();
    addHeader("Konuşma Detayları");

    const speakerItems = speakers?.map((speaker) => {
      const timeRange = `[${formatSeconds(speaker.start)}-${formatSeconds(speaker.end)}]`;
      return `${timeRange} ${speaker.speaker || "Konuşmacı"}: ${speaker.text || ""}`;
    });

    if (speakerItems?.length) {
      addList(speakerItems);
    } else {
      addList(["Konuşma detayları bulunamadı."]);
    }

    addFooter();
    doc.save(`${cleanTitle}_03_Konusma_Detaylari.pdf`);
  }
}

export async function exportMeetingSummaryPDF(params) {
  return exportMeetingToPDF({ ...params, section: "summary" });
}

export async function exportMeetingTranscriptPDF(params) {
  return exportMeetingToPDF({ ...params, section: "transcript" });
}

export async function exportMeetingSpeechDetailsPDF(params) {
  return exportMeetingToPDF({ ...params, section: "speech" });
}
